import * as Sentry from '@sentry/react'
import posthog from 'posthog-js'
import React from 'react'
import ReactDOM from 'react-dom/client'
import ErrorFallback from '@/components/ErrorFallback'
import {
  attemptChunkReload,
  isChunkLoadError,
  isPersistentChunkFailure,
} from '@/lib/chunkReload'

// Sentry init ANTES de cualquier import de App — captura errores incluso
// durante el bootstrap (módulos lazy, bundle errors). enabled:false cuando
// VITE_SENTRY_DSN no está seteado → SDK queda dormido, cero overhead.
// Build-time el DSN viaja como ARG → ENV en el Dockerfile del frontend-build;
// dev local con `npm run dev` lee de mytapstart_react/.env.local.
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN || undefined,
  environment: import.meta.env.VITE_APP_ENV || 'local',
  release: import.meta.env.VITE_GIT_SHA || undefined,
  enabled: !!import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration(),
  ],
  tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? '0.1'),
  // Session Replay de Sentry deshabilitado — usamos PostHog para replay.
  // Profiling también off (lo subimos a Sentry solo si surge un cuello de
  // botella JS reproducible que el RUM no acote).
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 0,
  // Stale chunk tras deploy (ver @/lib/chunkReload): el fallo de carga de un
  // chunk lazy es ruido transitorio de deploy, NO un bug. En el 1er fallo
  // recargamos (attemptChunkReload) y NO reportamos. Solo si tras recargar
  // SIGUE fallando (deploy roto de verdad) lo dejamos pasar, tageado.
  beforeSend(event, hint) {
    if (isChunkLoadError(hint?.originalException)) {
      if (!isPersistentChunkFailure()) return null;
      event.tags = { ...event.tags, chunk_load_persistent: true };
    }
    return event;
  },
})

// Vía principal de recuperación: Vite emite `vite:preloadError` en window
// cuando falla la carga de un módulo dinámico (páginas lazy + imports ad-hoc
// como el de Register). Recargamos una vez (guarda anti-bucle dentro) y
// evitamos que el evento propague como error no capturado.
window.addEventListener('vite:preloadError', (event) => {
  if (attemptChunkReload()) event.preventDefault();
})

Sentry.setTag('service', 'react')

// PostHog — product analytics + session replay con masking SELECTIVO. La
// Project API Key (phc_…) es pública por diseño y viaja en el bundle. Si
// VITE_POSTHOG_KEY queda vacía, posthog.init no se llama → SDK noop.
// Decisiones §41. Persistencia por defecto (localStorage + cookie) — el
// distinct_id del visitor anónimo se alinea con el user real al
// posthog.identify() del AuthContext en login/me.
if (import.meta.env.VITE_POSTHOG_KEY) {
  posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com',
    // autocapture: por defecto está ON. Captura clicks, submits y change
    // events con selectores estables (ids, data-attrs). Suficiente para
    // entender funnel onboarding/upgrade sin instrumentar a mano.
    autocapture: true,
    capture_pageview: true,
    capture_pageleave: true,
    // Persistir solo localStorage+cookie (default). NO cross-subdomain.
    persistence: 'localStorage+cookie',
    // Session recording: SIEMPRE OFF al init, en TODOS los entornos. Quién
    // graba lo decide `PosthogRecordingGate` (env + admin + impersonación +
    // rutas públicas de escaneo), que es la única fuente de verdad —
    // decisiones §41.7. Antes esto era `VITE_APP_ENV !== 'prod'`, pero tener
    // dos sitios decidiendo (aquí por env, el gate por usuario/ruta) hacía
    // que se pisaran: cualquier `startSessionRecording()` del gate reactivaba
    // la grabación en staging.
    //
    // Arrancar OFF y encender después es seguro (es exactamente lo que hace
    // `__ph.on()`); lo que NO vale es gatearlo con `opt_out_capturing()` al
    // boot — deja el subsistema de recording muerto y un opt_in posterior no
    // lo revive (el gotcha de §41.7).
    disable_session_recording: true,
    // Session recording con masking SELECTIVO (decisiones §41). Para un
    // SaaS B2B la mayoría del contenido es data de negocio (NO PII de
    // usuario final), así que mostramos todo por defecto y enmascaramos
    // solo lo sensible:
    //   - maskAllInputs: false → inputs visibles por defecto
    //   - sin maskTextSelector → texto de la app visible en el replay
    //   - maskInputFn enmascara SIEMPRE passwords + campos de pago, y
    //     cualquier campo cuyo ancestro tenga `data-ph-mask` (opt-in).
    // rrweb además enmascara `<input type=password>` por su cuenta — el
    // maskInputFn es belt + suspenders.
    session_recording: {
      maskAllInputs: false,
      // rrweb SOLO llama a maskInputFn para los tipos listados aquí. Por
      // defecto sería { password: true } → con el toggle "mostrar contraseña"
      // el type pasa a 'text', rrweb deja de invocar maskInputFn y graba el
      // valor en claro al teclear. Enrutamos también text/email/tel/number/
      // etc. a maskInputFn para que la lógica selectiva decida por campo
      // (devuelve el texto crudo para los no sensibles → siguen visibles).
      // NO quitar 'password': este objeto reemplaza al default de rrweb.
      maskInputOptions: {
        password: true,
        text: true,
        email: true,
        tel: true,
        number: true,
        search: true,
        url: true,
        textarea: true,
      },
      maskInputFn: (text, element) => {
        if (!element || !element.getAttribute) return text
        const mask = () => (text ? text.replace(/./g, '*') : text)
        const type = (element.getAttribute('type') || '').toLowerCase()
        const name = (element.getAttribute('name') || '').toLowerCase()
        const id = (element.getAttribute('id') || '').toLowerCase()
        const autocomplete = (element.getAttribute('autocomplete') || '').toLowerCase()

        // Passwords — detección robusta. NO basta con type==='password'
        // porque los forms tienen toggle "mostrar contraseña" que cambia el
        // type a 'text'. Detectamos también por id/name/autocomplete, que NO
        // cambian al togglear visibilidad. (Para que esta función llegue a
        // ejecutarse con type='text' es imprescindible el maskInputOptions de
        // arriba; sin él rrweb no la invocaría y la contraseña se vería en
        // claro al teclear con la contraseña visible.)
        if (
          type === 'password' ||
          /pass|contrase/.test(id) ||
          /pass|contrase/.test(name) ||
          /password/.test(autocomplete)
        ) {
          return mask()
        }

        // Opt-in explícito: campo (o su contenedor) marcado data-ph-mask.
        if (element.closest && element.closest('[data-ph-mask]')) {
          return mask()
        }

        // Campos de pago por hint de autocomplete/name (tarjeta, CVC...).
        // El checkout real es Stripe hosted (fuera de la app), pero por si
        // algún form custom toca datos de pago.
        if (/cc-|card|cvc|csc|cvv/.test(autocomplete) || /card|cvc|csc|cvv/.test(name)) {
          return mask()
        }

        return text
      },
    },
  })
}

// Expuesto en window para inspección desde la consola en cualquier
// entorno NO prod (dev de Vite, builds locales con APP_ENV=local, o
// staging). En prod queda sin global para no facilitar fingerprinting
// ni manipulación trivial desde consola.
if (import.meta.env.DEV || import.meta.env.VITE_APP_ENV !== 'prod') {
  window.posthog = posthog
  // Helper rápido para togglear session recording en no-prod (recording está
  // OFF por defecto en TODOS los entornos, ver arriba). NO se expone en prod.
  // `__ph.on()` sigue siendo la vía para probar replay en local/staging: el
  // gate es inerte fuera de prod a propósito, así que no lo apaga por detrás.
  window.__ph = {
    on: () => {
      posthog.startSessionRecording()
      console.log('▶ PostHog session recording ON')
    },
    off: () => {
      posthog.stopSessionRecording()
      console.log('⏹ PostHog session recording OFF')
    },
    status: () => {
      const rec = !!(posthog.sessionRecording && posthog.sessionRecording.started)
      console.log(`PostHog recording: ${rec ? 'ON ▶' : 'OFF ⏹'} · env=${import.meta.env.VITE_APP_ENV} · optedIn=${posthog.has_opted_in_capturing?.() ?? 'n/a'}`)
      return rec
    },
  }
}

import App from '@/App.jsx'
import { HIDDEN_CLASS as SUPPORT_CHAT_HIDDEN_CLASS } from '@/lib/supportChat'
import '@/index.css'
import i18n, { i18nListo } from './i18n';
import httpClient from '@/api/httpClient'
import { prepararIdiomaDeSesion } from '@/lib/idiomaSesion'

// El chat de soporte arranca oculto y es `<SupportChat>` quien lo destapa al
// montarse. Va aquí, antes del primer render, y no al revés: si la clase se
// pusiera al desmontar, entrar directo por /login enseñaría la burbuja hasta que
// React montara el árbol.
document.documentElement.classList.add(SUPPORT_CHAT_HIDDEN_CLASS)

function montar() {
  ReactDOM.createRoot(document.getElementById('root')).render(
    // Sentry ErrorBoundary captura errores no-cogidos del árbol React +
    // muestra el fallback (decisiones §39 item D). Sin esto, un throw en
    // cualquier componente blanquea la SPA entera.
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => <ErrorFallback error={error} resetError={resetError} />}
      // Backstop para chunk-errors que lleguen a React sin pasar por
      // `vite:preloadError`: tageamos e intentamos el reload guardado antes de
      // renderizar el fallback (si recarga, el fallback nunca llega a verse).
      beforeCapture={(scope, error) => {
        if (isChunkLoadError(error)) {
          scope.setTag('chunk_load', true)
          attemptChunkReload()
        }
      }}
      showDialog={false}
    >
      <App />
    </Sentry.ErrorBoundary>
  )
}

/*
 * Se monta cuando el idioma detectado está cargado.
 *
 * Desde que las traducciones dejaron de ir en el bundle, un visitante que no
 * sea español necesita un fichero más antes de poder pintar su idioma. Montar
 * sin esperarlo le daría un parpadeo: su página correcta (la prerenderizada),
 * luego español, luego su idioma.
 *
 * Y NO deja la pantalla en blanco mientras tanto: el HTML prerenderizado ya
 * está ahí, pintado y en su idioma. Lo único que se retrasa es sustituirlo por
 * el árbol de React.
 *
 * El `.catch` es la parte importante: si el chunk del idioma no carga, se monta
 * IGUAL y se ve todo en español. Quedarse sin montar por no poder traducir sería
 * cambiar un fallo leve por uno total.
 */
i18nListo
  .catch(() => {})
  // El idioma de la sesión (IP de Portugal → pt, y de la home española a /pt/)
  // va DESPUÉS de cargar el detectado y ANTES de montar: así el panel no
  // parpadea de español a portugués. `prepararIdiomaDeSesion` no lanza nunca y
  // su red tiene un tope de 1,5 s; el `.catch` de abajo es un cinturón más.
  // Ver `lib/idiomaSesion.js`.
  .then(() => prepararIdiomaDeSesion({ i18n, http: httpClient }))
  .catch(() => ({ redirigiendo: false }))
  .then((r) => {
    // Redirigiendo a /pt/: montar aquí sería pintar una página que el
    // navegador ya está abandonando.
    if (!r?.redirigiendo) montar()
  })

if (import.meta.hot) {
  import.meta.hot.on('vite:beforeUpdate', () => {
    window.parent?.postMessage({ type: 'sandbox:beforeUpdate' }, '*');
  });
  import.meta.hot.on('vite:afterUpdate', () => {
    window.parent?.postMessage({ type: 'sandbox:afterUpdate' }, '*');
  });
}



